import React from 'react';
import {
  PieChart as RechartsPieChart,
  Pie,
  Cell,
  Tooltip,
  ResponsiveContainer,
  Legend
} from 'recharts';
import { useTheme } from '@/hooks/useTheme';

interface PieChartProps {
  data: Array<{
    name: string;
    value: number;
    color?: string;
  }>;
  height?: number;
  className?: string;
  showLegend?: boolean;
  innerRadius?: number;
  outerRadius?: number;
  formatTooltip?: (value: any, name: string) => [string, string];
  colors?: string[];
}

const DEFAULT_COLORS = [
  '#3b82f6', // blue-500
  '#10b981', // emerald-500
  '#f59e0b', // amber-500
  '#ef4444', // red-500
  '#8b5cf6', // violet-500
  '#06b6d4', // cyan-500
  '#84cc16', // lime-500
  '#f97316', // orange-500
];

export const PieChart: React.FC<PieChartProps> = ({
  data,
  height = 300,
  className = '',
  showLegend = true,
  innerRadius = 0,
  outerRadius = 80,
  formatTooltip,
  colors = DEFAULT_COLORS
}) => {
  const { isDark } = useTheme();

  // Dark mode colors
  const tooltipBg = isDark ? '#18181b' : 'white';
  const tooltipBorder = isDark ? 'rgba(255, 255, 255, 0.1)' : '#e5e7eb';
  const tooltipLabelColor = isDark ? '#f4f4f5' : '#374151';
  const legendColor = isDark ? '#a1a1aa' : undefined;

  const defaultTooltipFormatter = (value: any, name: string) => {
    const total = data.reduce((sum, item) => sum + item.value, 0);
    const percentage = ((value / total) * 100).toFixed(1);
    
    if (name.toLowerCase().includes('revenue') || name.toLowerCase().includes('$')) {
      return [`$${Number(value).toLocaleString()} (${percentage}%)`, name];
    }
    return [`${Number(value).toLocaleString()} (${percentage}%)`, name];
  };

  // Add colors to data if not provided
  const dataWithColors = data.map((item, index) => ({
    ...item,
    color: item.color || colors[index % colors.length]
  }));

  const renderCustomLabel = (entry: any) => {
    const total = data.reduce((sum, item) => sum + item.value, 0);
    const percentage = ((entry.value / total) * 100).toFixed(0);
    return `${percentage}%`;
  };

  return (
    <div className={`w-full ${className}`}>
      <ResponsiveContainer width="100%" height={height}>
        <RechartsPieChart>
          <Pie
            data={dataWithColors}
            dataKey="value"
            nameKey="name"
            cx="50%"
            cy="50%"
            innerRadius={innerRadius}
            outerRadius={outerRadius}
            paddingAngle={2}
            label={renderCustomLabel}
            labelLine={false}
          >
            {dataWithColors.map((entry, index) => (
              <Cell key={`cell-${index}`} fill={entry.color} />
            ))}
          </Pie>
          <Tooltip
            formatter={formatTooltip || defaultTooltipFormatter}
            labelStyle={{ color: tooltipLabelColor, fontWeight: 'medium' }}
            contentStyle={{
              backgroundColor: tooltipBg,
              border: `1px solid ${tooltipBorder}`,
              borderRadius: '8px',
              boxShadow: isDark ? '0 4px 6px -1px rgba(0, 0, 0, 0.3)' : '0 4px 6px -1px rgba(0, 0, 0, 0.1)'
            }}
          />
          {showLegend && (
            <Legend
              verticalAlign="bottom"
              height={36}
              iconType="circle"
              wrapperStyle={{
                paddingTop: '20px',
                fontSize: '14px',
                color: legendColor
              }}
            />
          )}
        </RechartsPieChart>
      </ResponsiveContainer>
    </div>
  );
}; 